<template>
    <div class="bg-white p-6 rounded-lg shadow-md max-w-3xl mx-auto space-y-6">
        <!-- Bill Summary -->
        <div class="space-y-1">
            <h2 class="text-lg font-semibold text-gray-700 border-b pb-2 mb-3">Bill Summary</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><strong>Bill Number:</strong> {{ bills.bill_number }}</div>
                <div><strong>Total Amount:</strong> {{ config(common.currency) }} {{ bills.total_amount }}</div>
                <div><strong>Pending Amount:</strong> {{ config(common.currency) }} {{ bills.amount_remaining }}</div>
                <div><strong>Selected Method:</strong> {{ deliveredPayload.paymentType || 'N/A' }}</div>
            </div>
        </div>

        <!-- Payment Method Selector -->
        <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700">Payment Using</label>
            <t-radio-group
                v-model="paymentUsed"
                :classes="{
                    groupWrapper: 'flex gap-4',
                    label: 'ml-2 text-gray-700 text-sm font-semibold uppercase',
                    input: 'text-blue-500 border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500',
                }"
                :options="['CASH', 'BANK', 'BOTH']"
            />
        </div>

        <!-- Cash Section -->
        <div
            v-if="paymentUsed === 'CASH' || paymentUsed === 'BOTH'"
            class="space-y-1"
        >
            <label class="text-sm font-medium text-gray-700">Cash Amount</label>
            <t-input
                v-model="deliveredPayload.cashAmount"
                type="text"
                placeholder="Enter Cash Amount"
            />
        </div>

        <!-- Bank Section -->
        <div
            v-if="paymentUsed === 'BANK' || paymentUsed === 'BOTH'"
            class="space-y-4"
        >
            <div class="space-y-1">
                <label class="text-sm font-medium text-gray-700">Cheque Total Amount</label>
                <t-input
                    v-model="deliveredPayload.checkAmount"
                    type="text"
                    placeholder="Enter Cheque Total"
                />
            </div>

            <!-- Cheque Details -->
            <div class="flex items-center justify-between">
                <h3 class="text-md font-semibold text-gray-700">Cheque Details</h3>
                <t-button
                    type="button"
                    variant="success"
                    class="!text-xs !py-1 !px-3"
                    @click="addNewRow"
                >
                    Add Cheque
                </t-button>
            </div>

            <div
                v-for="(item, index) in deliveredPayload.checkPayments"
                :key="index"
                class="grid grid-cols-1 md:grid-cols-3 gap-4 border p-4 rounded"
            >
                <!-- Bank -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Bank</label>
                    <t-rich-select
                        v-model="item.bankId"
                        :options="bankList.data"
                        :clearable="true"
                        value-attribute="id"
                        text-attribute="name"
                        placeholder="Select Bank"
                    />
                </div>

                <!-- Cheque No -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Cheque No.</label>
                    <t-input
                        v-model="item.checkNumber"
                        type="text"
                        placeholder="Enter Cheque No."
                    />
                </div>

                <!-- Cheque Amount -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Cheque Amount</label>
                    <t-input
                        v-model="item.checkAmount"
                        type="text"
                        placeholder="Enter Amount"
                    />
                </div>
            </div>
        </div>

        <!-- Action Button -->
        <div class="pt-4 border-t">
            <t-button
                type="button"
                variant="success"
                class="w-full justify-center py-2"
                @click="changeStatusToDelivered"
            >
                <div class="flex items-center justify-center gap-2 font-semibold">
                    <span>Receive Payment</span>
                    <CheckIcon class="w-5" />
                </div>
            </t-button>
        </div>

        <!-- Notifications -->
        <portal
            v-if="show"
            to="notify"
        >
            <Notify
                :message="message"
                :status="status"
                @closeNotify="
                    show = false;
                    status = null;
                    message = null;
                "
            />
        </portal>
    </div>
</template>

<script>
    import axios from 'axios';
    import common from '../../common';
    import Notify from '../../Components/Notify.vue';

    export default {
        components: { Notify },
        props: {
            bills: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                billId: this.bills.id,
                show: false,
                status: null,
                message: null,
                common,
                paymentUsed: 'CASH',
                bankList: [],
                deliveredPayload: {
                    paymentType: this.bills.payment_method,
                    cashAmount: '',
                    checkAmount: '',
                    checkPayments: [
                        {
                            checkNumber: '',
                            checkAmount: '',
                            bankId: 0,
                        },
                    ],
                },
            };
        },

        async mounted() {
            await this.fetchBanks();
        },

        methods: {
            changeStatusToDelivered() {
                if (this.deliveredPayload.cashAmount == null || this.deliveredPayload.cashAmount === '') {
                    this.deliveredPayload.cashAmount = 0;
                }
                if (this.deliveredPayload.checkAmount == null || this.deliveredPayload.checkAmount === '') {
                    this.deliveredPayload.checkAmount = 0;
                }

                if (
                    this.deliveredPayload.checkPayments.length < 2 &&
                    this.deliveredPayload.checkPayments[0].bankId === 0
                ) {
                    this.deliveredPayload.checkPayments = [];
                }
                axios
                    .get(route('bill.delivered', this.billId), {
                        headers: {
                            Accept: 'application/json',
                        },
                        params: this.deliveredPayload,
                    })
                    .then((res) => {
                        this.show = true;
                        this.status = res.status;
                        this.message = res.data.success;
                        this.$emit('closeModal');
                        this.$emit('payment-complete');
                    })
                    .catch((ex) => {
                        this.show = true;
                        this.status = ex.response.status;
                        this.message = ex.response.data.error;
                    });
            },

            async fetchBanks() {
                await this.$axios.get(route('banks.index')).then((res) => {
                    this.bankList = res.data.banks;
                });
            },

            addNewRow() {
                const errorflag = this.validateChequeData();
                console.log(errorflag);
                if (errorflag) {
                    this.deliveredPayload.checkPayments.push({
                        checkNumber: '',
                        checkAmount: '',
                        bankId: 0,
                    });
                }
            },
            validateChequeData() {
                // eslint-disable-next-line consistent-return
                let result = true;
                this.deliveredPayload.checkPayments.forEach((checkPayment) => {
                    if (checkPayment !== undefined) {
                        if (checkPayment.checkNumber === '') {
                            result = false;
                        }
                        if (checkPayment.checkAmount === '') {
                            console.log(checkPayment.checkAmount);
                            result = false;
                        }
                        if (parseInt(checkPayment.bankId) === 0) {
                            console.log(checkPayment.bankId);
                            result = false;
                        }
                    } else {
                        result = false;
                    }
                });
                return result;
            },
        },
    };
</script>
